import React, { useState, useCallback, useMemo } from "react";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Loader2,
  Star,
  StarHalf,
  Navigation,
  Share2,
  X
} from "lucide-react";
import { GoogleMap, Marker, useJsApiLoader, type Libraries } from "@react-google-maps/api";
import ContactForm from "@/components/ContactForm";
import SEO from "@/components/seo/seo";

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};


const center = {
  lat: 25.187110045316885,
  lng: 55.2806119056373,
};

const libraries: Libraries = ["places"];

// Helper to format "HHMM" to "h:mm am/pm"
const formatTime = (timeStr: string) => {
  if (!timeStr) return "";
  const hours = parseInt(timeStr.substring(0, 2));
  const minutes = timeStr.substring(2);
  const ampm = hours >= 12 ? "pm" : "am";
  const h = hours % 12 || 12;
  return `${h}${minutes === "00" ? "" : ":" + minutes} ${ampm}`;
};

// Helper to get status text (Open . Closes 6 pm)
const getStatusText = (place: any) => {
  if (!place.opening_hours || !place.opening_hours.periods) {
    return null;
  }

  const isOpen = place.opening_hours.isOpen();
  // Calculate current time in place's timezone
  const now = new Date();
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const placeTime = new Date(utc + ((place.utc_offset_minutes || 240) * 60000));
  const currentDay = placeTime.getDay();
  const currentTime = placeTime.getHours() * 100 + placeTime.getMinutes();

  const periods = place.opening_hours.periods;

  if (isOpen) {
    // Find when it closes today
    // Look for a period that is currently active:
    // period.open.day == currentDay && period.open.time <= currentTime
    // OR period wraps from previous day
    // Easiest is to search for the *next* close event
    // But since it's open, we just want the close time of the current period.

    // Find period where we are currently insde [open, close)
    const activePeriod = periods.find((p: any) => {
      if (p.close && p.open) {
        // Check for same day
        if (p.open.day === currentDay && p.close.day === currentDay) {
          return currentTime >= parseInt(p.open.time) && currentTime < parseInt(p.close.time);
        }
        // Check for overnight (open today, close tomorrow)
        if (p.open.day === currentDay && p.close.day !== currentDay) {
          return currentTime >= parseInt(p.open.time);
        }
        // Check for overnight (open yesterday, close today)
        if (p.close.day === currentDay && p.open.day !== currentDay) {
          return currentTime < parseInt(p.close.time);
        }
      }
      return false;
    });

    if (activePeriod && activePeriod.close) {
      return (
        <span className="text-green-600 font-medium text-xs">
          Open <span className="text-gray-600">⋅ Closes {formatTime(activePeriod.close.time)}</span>
        </span>
      );
    }
    return <span className="text-green-600 font-medium text-xs">Open</span>;
  } else {
    // It's closed. Find next open time.
    // Find first period where open.day == currentDay AND open.time > currentTime
    // OR first period on next days

    // Sort periods by day/time to be safe, though usually sorted
    // We can just iterate days starting from today
    let nextOpen = null;

    for (let d = 0; d < 7; d++) {
      const checkDay = (currentDay + d) % 7;
      const potentialPeriods = periods.filter((p: any) => p.open.day === checkDay);

      // If checking today, must be after current time
      if (d === 0) {
        const next = potentialPeriods.find((p: any) => parseInt(p.open.time) > currentTime);
        if (next) {
          nextOpen = next;
          break;
        }
      } else {
        // Find first period of that day
        if (potentialPeriods.length > 0) {
          nextOpen = potentialPeriods[0];
          break;
        }
      }
    }

    if (nextOpen) {
      return (
        <span className="text-red-600 font-medium text-xs">
          Closed <span className="text-gray-600">⋅ Opens {formatTime(nextOpen.open.time)} {nextOpen.open.day !== currentDay ? ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][nextOpen.open.day] : ""}</span>
        </span>
      );
    }

    return <span className="text-red-600 font-medium text-xs">Closed</span>;
  }
};

const ContactPage = () => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);

  const mapOptions = useMemo(() => {
    if (!isLoaded || !window.google) return {};
    return {
      mapTypeControl: true,
      mapTypeControlOptions: {
        position: window.google.maps.ControlPosition.TOP_RIGHT,
      },
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: window.google.maps.ControlPosition.TOP_RIGHT,
      },
      streetViewControl: false,
      zoomControl: true,
    };
  }, [isLoaded]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMap(map);

    // Fetch place details based on coordinates
    if (window.google && window.google.maps && window.google.maps.places) {
      const service = new window.google.maps.places.PlacesService(map);

      // Search for the specific business to ensure we get rich data (ratings, hours)
      const request = {
        // query: `${center.lat}, ${center.lng}`,
        query: "Urban Oasis Real Estate Brokers",
        location: center,
        radius: 50
      };

      service.textSearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results[0]) {
          const placeId = results[0].place_id;

          service.getDetails({
            placeId: placeId!,
            fields: [
              "name",
              "formatted_address",
              "rating",
              "user_ratings_total",
              "geometry",
              "photos",
              "types",
              "opening_hours",
              "url",
              "utc_offset_minutes"
            ]
          }, (place, detailStatus) => {
            if (detailStatus === window.google.maps.places.PlacesServiceStatus.OK && place) {
              setSelectedPlace(place);
              setInfoWindowOpen(true);
            }
          });
        }
      });
    }
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  return (
    <>
      <SEO
        title="Contact Us | Urban Oasis UAE"
        description="Contact us for any inquiries about our properties or services. Our team is here to help you find your dream property in Dubai."
        url="/contact"
        image="/image/logo.png"
        keywords="contact, dubai real estate, urban oasis"
      />
      <div className="flex-grow pt-32 pb-16">
        <div className="container mx-auto px-4 mb-16">
          <div className="mb-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Contact Us</h1>
            <p className="text-muted-foreground max-w-2xl">
              Have questions about our properties or services? Our team is here to help you find your dream property in Dubai.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Contact Information */}
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Our Office</h3>
                    <p className="text-muted-foreground">
                      Office 1862, Tamani arts office building<br />
                      BusinessBay, UAE
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Phone</h3>
                    <p className="text-muted-foreground">+971 58 899 5586</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Email</h3>
                    <p className="text-muted-foreground">leads@urbanoasis.ae</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Working Hours</h3>
                    <p className="text-muted-foreground">
                      Sunday - Thursday: 9:00 AM - 6:00 PM<br />
                      Friday - Saturday: 10:00 AM - 2:00 PM
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <ContactForm />
          </div>
        </div>

        {/* Map Section */}
        <div className="">
          <div className="container mx-auto px-4 text-center mb-8">
            <h2 className="text-2xl font-semibold mb-2">Visit Our Office</h2>
            <p className="text-muted-foreground">Located in the heart of Business Bay, Dubai</p>
          </div>
          <div className="container mx-auto px-4">
            <div className="bg-gray-200 h-[500px] rounded-xl overflow-hidden shadow-lg relative">
              {isLoaded ? (
                <>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={center}
                    zoom={16}
                    onLoad={onMapLoad}
                    onUnmount={onUnmount}
                    options={mapOptions}
                  >
                    <Marker
                      position={center}
                      onClick={() => setInfoWindowOpen(true)}
                    />
                  </GoogleMap>

                  {/* Custom Card Overlay */}
                  {infoWindowOpen && selectedPlace && (
                    <div className="absolute top-4 left-4 z-10 bg-white rounded-lg shadow-xl w-[320px] overflow-hidden font-sans animation-fade-in">
                      {/* Close Button */}
                      <button
                        onClick={() => setInfoWindowOpen(false)}
                        className="absolute top-2 right-2 z-20 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>

                      {/* Image */}
                      <div className="h-40 w-full overflow-hidden relative bg-gray-100">
                        {selectedPlace.photos && selectedPlace.photos.length > 0 ? (
                          <img
                            src={selectedPlace.photos[0].getUrl({ maxWidth: 400, maxHeight: 200 })}
                            alt={selectedPlace.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <img
                            src={`https://maps.googleapis.com/maps/api/streetview?size=400x200&location=${center.lat},${center.lng}&fov=80&heading=70&pitch=0&key=${import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY}`}
                            alt="Location Street View"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>

                      <div className="p-4">
                        {/* Title & Directions */}
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 leading-tight">
                            {selectedPlace.name}
                          </h3>
                          <a
                            href={selectedPlace.url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("Urban Oasis Real Estate Brokers")}&query_place_id=${selectedPlace.place_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-none bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full transition-colors group shadow-sm"
                            title="Get Directions"
                          >
                            <Navigation className="w-4 h-4 fill-current opacity-90" />
                          </a>
                        </div>

                        {/* Rating */}
                        <div className="flex items-center gap-1 mb-2 text-sm">
                          <span className="font-medium text-gray-900">
                            {selectedPlace.rating ? selectedPlace.rating.toFixed(1) : "N/A"}
                          </span>
                          <div className="flex text-amber-400">
                            {[...Array(5)].map((_, i) => {
                              const rating = selectedPlace.rating || 0;
                              // Round to nearest 0.5
                              const roundedRating = Math.round(rating * 2) / 2;

                              if (roundedRating >= i + 1) {
                                return <Star key={i} className="w-3.5 h-3.5 fill-current text-amber-400" />;
                              } else if (roundedRating >= i + 0.5) {
                                return <StarHalf key={i} className="w-3.5 h-3.5 fill-current text-amber-400" />;
                              } else {
                                return <Star key={i} className="w-3.5 h-3.5 text-gray-300" />;
                              }
                            })}
                          </div>
                          <span className="text-gray-500">
                            ({selectedPlace.user_ratings_total ? selectedPlace.user_ratings_total : 0})
                          </span>
                        </div>

                        {/* Address */}
                        <div className="text-xs text-gray-500 mb-1.5 leading-relaxed">
                          {/* {selectedPlace.types && selectedPlace.types.length > 0 && (
                            <span className="block mb-0.5 text-gray-400 capitalize">{selectedPlace.types[0].replace(/_/g, " ")}</span>
                          )} */}
                          {selectedPlace.formatted_address}
                        </div>

                        {/* Opening Hours */}
                        <div className="text-xs text-gray-600 flex flex-wrap gap-1">
                          {selectedPlace.opening_hours && (
                            <>
                              {getStatusText(selectedPlace)}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-secondary/20">
                  <div className="text-center">
                    <Loader2 className="h-12 w-12 text-primary mx-auto mb-3 animate-spin" />
                    <p className="font-medium">Loading Map...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;
